import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Filter, ChevronDown, ChevronRight, Pin, Star, User, Bot as BotIcon, Search, GitBranch } from "lucide-react";
import { useState } from "react";
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CompactEditorCard } from "./CompactEditorCard";
import { CurrentFocusContainerMementoCard } from "./CurrentFocusContainerMementoCard";
import { MementoTimeline } from "./MementoTimeline";
import { Message } from "@/types/message";
import { CurrentFocusContainerMemento } from "@/types/memento";
import versionData from "../../../version.json";

export type { Message };

interface SortableCardProps {
  message: Message;
  onFocus: () => void;
  onClose: () => void;
  onPin: (messageId: string, pinned: boolean) => Promise<void>;
  onFavorite: (messageId: string, favorited: boolean) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
}

function SortableCard({
  message,
  onFocus,
  onClose,
  onPin,
  onFavorite,
  onDelete
}: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: message.id,
    disabled: !message.pinned
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const status = message.editor_status === 'wip' ? 'wip' : message.editor_status === 'closed' ? 'closed' : 'editing';

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CompactEditorCard
        message={message}
        status={status}
        onFocus={onFocus}
        onClose={onClose}
        onPin={onPin}
        onFavorite={onFavorite}
        onDelete={onDelete}
      />
    </div>
  );
}

interface HistoryPanelProps {
  messages: Message[];
  mementos: CurrentFocusContainerMemento[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  onFocusEditor: (messageId: string) => void;
  onLoadMemento: (memento: CurrentFocusContainerMemento) => void;
  onPin: (messageId: string, pinned: boolean) => Promise<void>;
  onFavorite: (messageId: string, favorited: boolean) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  onPinMemento: (mementoId: string) => Promise<void>;
  onFavoriteMemento: (mementoId: string) => Promise<void>;
  onDeleteMemento: (mementoId: string) => Promise<void>;
  onLoadMoreMementos: () => void;
  hasMoreMementos: boolean;
  loadingMementos: boolean;
  sensors: any;
  onDragEnd: (event: DragEndEvent) => void;
}

export function HistoryPanel({
  messages,
  mementos,
  activeTab,
  onTabChange,
  onFocusEditor,
  onLoadMemento,
  onPin,
  onFavorite,
  onDelete,
  onPinMemento,
  onFavoriteMemento,
  onDeleteMemento,
  onLoadMoreMementos,
  hasMoreMementos,
  loadingMementos,
  sensors,
  onDragEnd
}: HistoryPanelProps) {
  // Phase 2.2: Smart Filtering state
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'all'>('today');
  const [showAutoSaves, setShowAutoSaves] = useState(false); // Default HIDE auto-saves
  const [groupedAutoSaves, setGroupedAutoSaves] = useState(true);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [showFavoritedOnly, setShowFavoritedOnly] = useState(false);
  const [showVersionedOnly, setShowVersionedOnly] = useState(false); // Default SHOW all
  const [showDeleted, setShowDeleted] = useState(false); // Default HIDE deleted
  const [showHuman, setShowHuman] = useState(true); // Default SHOW human
  const [showBot, setShowBot] = useState(true); // Default SHOW bot
  const [searchText, setSearchText] = useState(''); // Text search
  
  // Phase 2.2: Time-based filtering
  const filterByTime = (items: HistoryItem[]) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    return items.filter(item => {
      const itemDate = new Date(item.created_at);
      
      if (timeFilter === 'today') {
        return itemDate >= todayStart;
      } else if (timeFilter === 'week') {
        return itemDate >= weekStart;
      }
      return true; // 'all'
    });
  };

  // Phase 2.2: Filter out auto-saves if not shown
  const filterAutoSaves = (items: HistoryItem[]) => {
    if (showAutoSaves) return items;
    
    return items.filter(item => {
      if (item.type === 'memento') {
        // Keep manual saves, stashes, and pinned/favorited
        return item.trigger_type !== 'auto_save' || item.pinned || item.favorited;
      }
      return true; // Keep all messages
    });
  };
  
  // Combine messages and mementos, sorted by created_at
  type HistoryItem = (Message & { type: 'message' }) | (CurrentFocusContainerMemento & { type: 'memento' });
  
  let allItems: HistoryItem[] = [
    ...messages.map(m => ({ ...m, type: 'message' as const })),
    ...mementos.map(m => ({ ...m, type: 'memento' as const }))
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Apply filters
  allItems = filterByTime(allItems);
  allItems = filterAutoSaves(allItems);

  const filterMessages = (status?: string, favorited?: boolean) => {
    let filtered = messages;
    if (status) {
      filtered = filtered.filter(m => m.editor_status === status);
    }
    if (favorited) {
      filtered = filtered.filter(m => m.favorited === true);
    }
    const pinnedMessages = filtered.filter(m => m.pinned).sort((a, b) => (a.pinned_order || 0) - (b.pinned_order || 0));
    const unpinnedMessages = filtered.filter(m => !m.pinned).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    return [...pinnedMessages, ...unpinnedMessages];
  };

  const allMessages = filterMessages();
  const wipMessages = filterMessages('wip');
  const closedMessages = filterMessages('closed');
  const favoritedMessages = filterMessages(undefined, true);
  
  // Also include mementos in "All" and "Favorites" tabs
  const allWithMementos = allItems;
  const favoritedMementos = mementos.filter(m => m.favorited);

  const renderHistoryList = (items: HistoryItem[]) => {
    const pinned = items.filter(item => 
      item.type === 'message' ? item.pinned : item.pinned
    );
    const unpinned = items.filter(item =>
      item.type === 'message' ? !item.pinned : !item.pinned
    );

    return (
      <>
        {pinned.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={pinned.map(item => item.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3 mb-4">
                {pinned.map(item => {
                  if (item.type === 'message') {
                    return (
                      <SortableCard
                        key={item.id}
                        message={item}
                        onFocus={() => onFocusEditor(item.id)}
                        onClose={() => {}}
                        onPin={onPin}
                        onFavorite={onFavorite}
                        onDelete={onDelete}
                      />
                    );
                  } else {
                    return (
                      <div key={item.id}>
                        <CurrentFocusContainerMementoCard
                          memento={item}
                          onLoad={onLoadMemento}
                          onPin={onPinMemento}
                          onFavorite={onFavoriteMemento}
                          onDelete={onDeleteMemento}
                          status={item.pinned ? 'pinned' : item.favorited ? 'favorite' : 'normal'}
                        />
                      </div>
                    );
                  }
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {unpinned.length > 0 && (
          <div className="space-y-3">
            {unpinned.map(item => {
              if (item.type === 'message') {
                const status = item.editor_status === 'wip' ? 'wip' : item.editor_status === 'closed' ? 'closed' : 'editing';
                return (
                  <CompactEditorCard
                    key={item.id}
                    message={item}
                    status={status}
                    onFocus={() => onFocusEditor(item.id)}
                    onClose={() => {}}
                    onPin={onPin}
                    onFavorite={onFavorite}
                    onDelete={onDelete}
                  />
                );
              } else {
                return (
                  <CurrentFocusContainerMementoCard
                    key={item.id}
                    memento={item}
                    onLoad={onLoadMemento}
                    onPin={onPinMemento}
                    onFavorite={onFavoriteMemento}
                    onDelete={onDeleteMemento}
                    status={item.pinned ? 'pinned' : item.favorited ? 'favorite' : 'normal'}
                  />
                );
              }
            })}
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            No items
          </div>
        )}
      </>
    );
  };

  const renderMessageList = (messageList: Message[]) => {
    return renderHistoryList(messageList.map(m => ({ ...m, type: 'message' as const })));
  };

  return (
    <div className="flex flex-col h-full overflow-hidden min-w-0">
      <div className="border-b px-4 py-3 shrink-0">
        <h2 className="text-lg font-semibold truncate">
          History <span className="text-xs text-muted-foreground font-normal">v{versionData.version}</span>
        </h2>
      </div>

      {/* Combined Filter Controls with Tab Selection */}
      <div className="border-b px-4 py-2 shrink-0 bg-muted/30">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          
          {/* Only show time/auto-save filters on Timeline tab */}
          {activeTab === 'timeline' && (
            <>
              <div className="h-4 w-px bg-border mx-1" />
              <div className="flex items-center gap-1">
                <Button
                  variant={timeFilter === 'today' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeFilter('today')}
                  className="h-7 text-xs"
                  data-testid="filter-time-today"
                  data-active={timeFilter === 'today'}
                >
                  Today
                </Button>
                <Button
                  variant={timeFilter === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeFilter('week')}
                  className="h-7 text-xs"
                  data-testid="filter-time-week"
                  data-active={timeFilter === 'week'}
                >
                  This Week
                </Button>
                <Button
                  variant={timeFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeFilter('all')}
                  className="h-7 text-xs"
                  data-testid="filter-time-all"
                  data-active={timeFilter === 'all'}
                >
                  All Time
                </Button>
              </div>
              <div className="h-4 w-px bg-border mx-1" />
              
              {/* Search box */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="h-7 text-xs pl-7 pr-2 w-32"
                  data-testid="search-input"
                />
              </div>
              
              <div className="h-4 w-px bg-border mx-1" />
              
              {/* Auto-saves toggle - inverted logic (hide by default) */}
              <Button
                variant={!showAutoSaves ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setShowAutoSaves(!showAutoSaves)}
                className="h-7 text-xs"
                title={showAutoSaves ? "Hide minor auto-saves" : "Show all auto-saves"}
                data-testid="filter-autosaves"
                data-active={showAutoSaves.toString()}
                data-variant={!showAutoSaves ? 'secondary' : 'ghost'}
              >
                {showAutoSaves ? 'Hide' : 'Show'} Auto-saves
              </Button>
              
              <div className="h-4 w-px bg-border mx-1" />
              
              {/* Bot/Human filters */}
              <Button
                variant={showBot ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setShowBot(!showBot)}
                className="h-7 text-xs px-2"
                title={showBot ? "Hide bot-created" : "Show bot-created"}
                data-testid="filter-bot"
                data-active={showBot.toString()}
                data-variant={showBot ? 'secondary' : 'ghost'}
              >
                <BotIcon className={`h-3.5 w-3.5 ${showBot ? 'text-blue-500' : ''}`} />
              </Button>
              <Button
                variant={showHuman ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setShowHuman(!showHuman)}
                className="h-7 text-xs px-2"
                title={showHuman ? "Hide human-created" : "Show human-created"}
                data-testid="filter-human"
                data-active={showHuman.toString()}
                data-variant={showHuman ? 'secondary' : 'ghost'}
              >
                <User className={`h-3.5 w-3.5 ${showHuman ? 'text-green-500' : ''}`} />
              </Button>
              
              <div className="h-4 w-px bg-border mx-1" />
              
              {/* Pin/Favorite/Version/Deleted toggles */}
              <Button
                variant={showPinnedOnly ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => {
                  setShowPinnedOnly(!showPinnedOnly);
                  if (!showPinnedOnly) {
                    setShowFavoritedOnly(false);
                    setShowVersionedOnly(false);
                  }
                }}
                className="h-7 text-xs px-2"
                title={showPinnedOnly ? "Show all" : "Show pinned only"}
                data-testid="filter-pin"
                data-active={showPinnedOnly.toString()}
                data-variant={showPinnedOnly ? 'secondary' : 'ghost'}
              >
                <Pin className={`h-3.5 w-3.5 ${showPinnedOnly ? 'fill-current text-yellow-500' : ''}`} />
              </Button>
              <Button
                variant={showFavoritedOnly ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => {
                  setShowFavoritedOnly(!showFavoritedOnly);
                  if (!showFavoritedOnly) {
                    setShowPinnedOnly(false);
                    setShowVersionedOnly(false);
                  }
                }}
                className="h-7 text-xs px-2"
                title={showFavoritedOnly ? "Show all" : "Show favorited only"}
                data-testid="filter-favorite"
                data-active={showFavoritedOnly.toString()}
                data-variant={showFavoritedOnly ? 'secondary' : 'ghost'}
              >
                <Star className={`h-3.5 w-3.5 ${showFavoritedOnly ? 'fill-current text-red-500' : ''}`} />
              </Button>
              <Button
                variant={showVersionedOnly ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => {
                  setShowVersionedOnly(!showVersionedOnly);
                  if (!showVersionedOnly) {
                    setShowPinnedOnly(false);
                    setShowFavoritedOnly(false);
                  }
                }}
                className="h-7 text-xs px-2"
                title={showVersionedOnly ? "Show all" : "Show versions only"}
                data-testid="filter-version"
                data-active={showVersionedOnly.toString()}
                data-variant={showVersionedOnly ? 'secondary' : 'ghost'}
              >
                <GitBranch className={`h-3.5 w-3.5 ${showVersionedOnly ? 'text-purple-500' : ''}`} />
              </Button>
              <Button
                variant={showDeleted ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setShowDeleted(!showDeleted)}
                className="h-7 text-xs"
                title={showDeleted ? "Hide deleted" : "Show deleted"}
                data-testid="filter-deleted"
                data-active={showDeleted.toString()}
                data-variant={showDeleted ? 'secondary' : 'ghost'}
              >
                {showDeleted ? 'Hide' : 'Show'} Deleted
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={onTabChange} className="flex flex-col flex-1 min-h-0">
        {/* Hide the default tab list */}
        <div className="hidden">
          <TabsList>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>
        </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {/* Phase 2.3: Timeline Tab */}
            <TabsContent value="timeline" className="h-full m-0 overflow-y-auto data-[state=inactive]:hidden">
              <MementoTimeline
                mementos={mementos}
                onSelectMemento={onLoadMemento}
                selectedMementoId={undefined}
                timeFilter={timeFilter}
                showAutoSaves={showAutoSaves}
                showPinnedOnly={showPinnedOnly}
                showFavoritedOnly={showFavoritedOnly}
                showVersionedOnly={showVersionedOnly}
                showDeleted={showDeleted}
                showHuman={showHuman}
                showBot={showBot}
                searchText={searchText}
                isActive={activeTab === 'timeline'}
                onLoadMore={onLoadMoreMementos}
                hasMore={hasMoreMementos}
                isLoading={loadingMementos}
              />
            </TabsContent>
          </div>
        </Tabs>
    </div>
  );
}
